import React, { useState } from 'react';
import {
  HStack,
  Button,
  VStack,
  // Stack,
  // Skeleton,
  // Spinner,
  Text,
} from '@chakra-ui/react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';

import ClassComponent from '../../components/ClassComponent';
import ClassPage from '../Class';
import { Query } from '../../../../generated/graphqlModels';
import { Role, User } from '../../../../context/AuthProvider';
import { trackAction } from '../../../../helpers/trackAction';
import { useTranslation } from 'react-i18next';
import { StemiBeatLoader } from '../../../../components/Loader';

const updateQuery = (previousResult: any, { fetchMoreResult }: any) => {
  if (!fetchMoreResult) {
    return previousResult;
  }

  const previousEdges = previousResult.studyGroups;
  const fetchMoreEdges = fetchMoreResult.studyGroups;

  fetchMoreResult.studyGroups = [...previousEdges, ...fetchMoreEdges];

  return {
    ...fetchMoreResult,
    pinnings: previousResult.pinnings,
  };
};

interface VirtualClassroomsProps {
  user: User | null;
  data: Query | undefined;
  loading: boolean;
  loadCount: number;
  setLoadCount: (_: number) => void;
  fetchMore: (opts: any) => Promise<any>;
  LOAD_INCREMENT: number;
}

const VirtualClassrooms: React.FC<VirtualClassroomsProps> = (props) => {
  const {
    user,
    data,
    loading,
    fetchMore,
    loadCount,
    setLoadCount,
    LOAD_INCREMENT,
  } = props;

  const [fetchingMore, setFetchingMore] = useState<boolean>(false);
  const { t } = useTranslation();

  const hasMore =
    data && data.studyGroups && loadCount <= data.studyGroups.length;

  const isStaff = user?.roles.includes(Role.staff) || false;

  if (!user || !user.pk) {
    return null;
  }

  return (
    <VStack
      align="start"
      gap={4}
      borderTop="1px solid #E0E0E0"
      marginTop="20px"
      paddingTop="20px"
    >
      <Text as="h4" textStyle="h4">
        {t('section.classrooms')}
      </Text>
      {loading && (
        <div style={{ width: '100%', textAlign: 'center' }}>
          <StemiBeatLoader />
        </div>
      )}
      <VStack gap={2}>
        {data &&
          data.studyGroups &&
          // @ts-ignore
          data.studyGroups
            ?.filter((s) => !s.isSelfLearning && !s.isExplore)
            ?.map((studyGroup, index) => {
              const expireDate = new Date(studyGroup.license.period.lastDate);
              expireDate.setHours(23, 59, 59);
              const isExpired = expireDate < new Date();

              return isExpired ? (
                <Tippy
                  arrow={true}
                  theme="light"
                  animation="scale"
                  delay={250}
                  content={t('oldDashboardPage.licenseExpired')}
                  key={studyGroup.id}
                >
                  <div style={{ width: '100%' }}>
                    <ClassComponent
                      to={ClassPage.getRoute(
                        studyGroup.license.program.id,
                        'units',
                        `?study-group-id=${studyGroup.id}`,
                      )}
                      {...studyGroup.license.program}
                      name={`${studyGroup.license.program.name} - ${studyGroup.name}`}
                      enabled={isStaff || false}
                      sameHeight={false}
                      showInfo={index === 0}
                    />
                  </div>
                </Tippy>
              ) : (
                <ClassComponent
                  key={studyGroup.id}
                  to={ClassPage.getRoute(
                    studyGroup.license.program.id,
                    'units',
                    `?study-group-id=${studyGroup.id}`,
                  )}
                  {...studyGroup.license.program}
                  name={`${studyGroup.license.program.name} - ${studyGroup.name}`}
                  enabled={user['pages']['study_group']}
                  sameHeight={false}
                  showInfo={index === 0}
                />
              );
            })}
        {data &&
          data.studyGroups &&
          data.studyGroups?.filter((s) => !s.isSelfLearning && !s.isExplore)
            .length === 0 && (
            <Text>{t('virtualClassroomsPage.noClassroomsAskToBeAdded')}</Text>
          )}
      </VStack>
      {hasMore && (
        <HStack justify="center" w="full">
          <Button
            size="sm"
            colorScheme="blue"
            isLoading={loading || fetchingMore}
            onClick={() => {
              setFetchingMore(true);
              fetchMore({
                variables: {
                  limit: LOAD_INCREMENT,
                  offset: loadCount,
                  loadPinnings: false,
                },
                updateQuery,
              })
                .then(() => {
                  setLoadCount(loadCount + LOAD_INCREMENT);
                })
                .finally(() => {
                  setFetchingMore(false);
                });

              trackAction({
                action_type: 'button_click',
                data: { url: window.location.pathname, text: 'Load more' },
              });
            }}
          >
            {t('button.loadMore')}
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            isLoading={loading || fetchingMore}
            onClick={() => {
              setFetchingMore(true);
              fetchMore({
                variables: {
                  limit: 5000,
                  offset: loadCount,
                  loadPinnings: false,
                },
                updateQuery,
              })
                .then(() => {
                  setLoadCount(loadCount + 5000);
                })
                .finally(() => {
                  setFetchingMore(false);
                });

              trackAction({
                action_type: 'button_click',
                data: { url: window.location.pathname, text: 'Load all' },
              });
            }}
          >
            {t('button.loadAll')}
          </Button>
        </HStack>
      )}
    </VStack>
  );
};

export default VirtualClassrooms;
export { VirtualClassrooms };
